import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, TrendingUp, TrendingDown, RotateCcw, History, X, Plus, LineChart as LineChartIcon } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell, Legend } from "recharts";
import { api } from "@/lib/api";
import { BIST_QUICK } from "@/lib/quickSymbols";

const SECTOR_COLORS = ["#2D6BFF", "#F5C542", "#46C06F", "#E5484D", "#7FA8FF", "#B084F0", "#FF9F45", "#22B0B0", "#D46A6A", "#8BC34A", "#FF5A8C", "#FFC966"];

async function _loadSectorMap() {
  // We fetch heatmap for a broad universe to build sector mapping used only for present positions
  try {
    const { data } = await api.get(`/heatmap?universe=bist100`);
    const map = {};
    (data.items || []).forEach((it) => { if (it.sector) map[it.symbol] = it.sector; });
    return map;
  } catch { return {}; }
}

export default function PortfolioPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trades, setTrades] = useState([]);
  const [equity, setEquity] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [tradeModal, setTradeModal] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [p, e] = await Promise.all([api.get("/portfolio"), api.get("/portfolio/equity-history")]);
      setData(p.data);
      setEquity((e.data.items || []).map((s) => ({
        at: s.at, label: new Date(s.at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }),
        equity: Number(s.total_equity), pnl_pct: Number(s.pnl_pct),
      })));
    } catch { toast.error("Portföy yüklenemedi"); }
    finally { setLoading(false); }
  };
  const loadTrades = async () => {
    try { const { data } = await api.get("/portfolio/history"); setTrades(data.items || []); } catch {}
  };
  useEffect(() => { load(); loadTrades(); }, []);

  const reset = async () => {
    if (!window.confirm("Portföy sıfırlansın mı? Tüm pozisyonlar silinecek ve bakiye 100.000₺'ye dönecek.")) return;
    try { await api.post("/portfolio/reset"); toast.success("Portföy sıfırlandı"); load(); loadTrades(); }
    catch { toast.error("Sıfırlanamadı"); }
  };

  if (loading) return <div className="p-16 flex justify-center"><div className="radar-loader" /></div>;
  if (!data) return null;

  const pnlPos = data.total_pnl >= 0;
  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary ztan-title flex items-center gap-2">
            <Wallet size={22} className="text-brand-blue" /> Portföy Simülasyonu
          </h1>
          <p className="text-sm text-text-secondary mt-1">Kağıt üzerinde BIST alım-satımı — gerçek para değil</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="portfolio-new-trade-btn"
            onClick={() => setTradeModal({ symbol: "", side: "buy" })}
            className="bg-accent-yellow text-bg-base font-semibold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 hover:opacity-90"
          ><Plus size={14} /> Yeni İşlem</button>
          <button
            data-testid="portfolio-history-btn"
            onClick={() => setShowHistory(true)}
            className="bg-bg-hover text-text-primary border border-bg-border px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 hover:bg-bg-border"
          ><History size={13} /> Geçmiş</button>
          <button
            data-testid="portfolio-reset-btn"
            onClick={reset}
            className="text-text-secondary hover:text-signal-down text-xs flex items-center gap-1.5 px-2"
          ><RotateCcw size={12} /> Sıfırla</button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Toplam Sermaye" value={`₺${fmt(data.total_equity)}`} sub={`Başlangıç: ₺${fmt(data.starting_cash, 0)}`} data-testid="tile-equity" />
        <Tile label="Nakit Bakiye" value={`₺${fmt(data.cash)}`} sub="Kullanılabilir" data-testid="tile-cash" />
        <Tile label="Pozisyon Değeri" value={`₺${fmt(data.total_position_value)}`} sub={`${data.positions.length} hisse`} data-testid="tile-positions" />
        <Tile
          label="Toplam K/Z"
          value={`${pnlPos ? "+" : ""}₺${fmt(data.total_pnl)}`}
          sub={`${pnlPos ? "+" : ""}${data.total_pnl_pct.toFixed(2)}%`}
          tone={pnlPos ? "up" : "down"}
          icon={pnlPos ? TrendingUp : TrendingDown}
          data-testid="tile-pnl"
        />
      </div>

      {/* Equity curve */}
      {equity.length >= 2 && (
        <div className="bg-bg-panel border border-bg-border rounded-xl p-4" data-testid="portfolio-equity-chart">
          <h3 className="text-sm font-semibold text-text-primary flex items-center gap-1.5 mb-3">
            <LineChartIcon size={14} className="text-brand-blue" /> Sermaye Grafiği (İşlem Bazlı)
          </h3>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={equity} margin={{ top: 8, right: 12, left: 8, bottom: 8 }}>
                <CartesianGrid stroke="rgb(var(--bg-hover))" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="rgb(var(--text-secondary))" tick={{ fontSize: 10 }} />
                <YAxis stroke="rgb(var(--text-secondary))" tick={{ fontSize: 10 }} domain={["dataMin", "dataMax"]}
                  tickFormatter={(v) => `₺${(v/1000).toFixed(0)}k`} />
                <ReferenceLine y={data.starting_cash} stroke="rgb(var(--brand-blue))" strokeDasharray="4 4" label={{ value: "Başlangıç ₺100k", fill: "rgb(var(--brand-blueLight))", fontSize: 10, position: "insideTopRight" }} />
                <RTooltip
                  contentStyle={{ background: "rgb(var(--bg-panel))", border: "1px solid rgb(var(--bg-border))", borderRadius: 8, color: "rgb(var(--text-primary))" }}
                  formatter={(v) => [`₺${Number(v).toLocaleString("tr-TR", {minimumFractionDigits:2, maximumFractionDigits:2})}`, "Sermaye"]}
                />
                <Line type="monotone" dataKey="equity" stroke="#F5C542" strokeWidth={2.5} dot={{ r: 3, fill: "#F5C542" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Sector allocation */}
      {data.positions.length > 0 && <SectorPie positions={data.positions} />}

      {/* Positions table */}
      <div className="bg-bg-panel border border-bg-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-bg-border">
          <h3 className="text-sm font-semibold text-text-primary">Açık Pozisyonlar</h3>
        </div>
        {data.positions.length === 0 ? (
          <div className="p-12 text-center">
            <Wallet size={26} className="text-text-secondary mx-auto mb-2 opacity-60" />
            <div className="text-sm text-text-primary font-medium">Henüz pozisyon yok</div>
            <div className="text-xs text-text-secondary mt-1">Yeni İşlem ile ilk alımınızı yapın</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary text-xs uppercase tracking-wider border-b border-bg-border">
                  <th className="py-3 px-4 text-left">Hisse</th>
                  <th className="py-3 px-4 text-right">Adet</th>
                  <th className="py-3 px-4 text-right">Ort. Maliyet</th>
                  <th className="py-3 px-4 text-right">Son Fiyat</th>
                  <th className="py-3 px-4 text-right">Piyasa Değeri</th>
                  <th className="py-3 px-4 text-right">K/Z</th>
                  <th className="py-3 px-4 text-center">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {data.positions.map((p) => (
                  <tr key={p.symbol} data-testid={`position-row-${p.symbol}`} className="border-b border-bg-border hover:bg-bg-hover transition-colors">
                    <td className="py-3 px-4">
                      <button onClick={() => nav(`/stock/${p.symbol}`)} className="text-left">
                        <div className="font-mono font-bold text-text-primary">{p.symbol}</div>
                        <div className="text-[10px] text-text-secondary">{p.name}</div>
                      </button>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-text-primary">{fmt(p.quantity, 0)}</td>
                    <td className="py-3 px-4 text-right font-mono text-text-primary">₺{fmt(p.avg_price)}</td>
                    <td className="py-3 px-4 text-right font-mono text-text-primary">₺{fmt(p.current_price)}</td>
                    <td className="py-3 px-4 text-right font-mono text-text-primary">₺{fmt(p.market_value)}</td>
                    <td className={`py-3 px-4 text-right font-mono font-medium ${p.pnl >= 0 ? "text-signal-up" : "text-signal-down"}`}>
                      {p.pnl >= 0 ? "+" : ""}₺{fmt(p.pnl)}
                      <div className="text-[10px]">{p.pnl_pct >= 0 ? "+" : ""}{p.pnl_pct.toFixed(2)}%</div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        data-testid={`sell-btn-${p.symbol}`}
                        onClick={() => setTradeModal({ symbol: p.symbol, side: "sell", maxQty: p.quantity })}
                        className="bg-bg-hover text-signal-down border border-signal-down/30 hover:bg-signal-down/10 text-xs px-3 py-1 rounded"
                      >Sat</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {tradeModal && (
        <TradeModal
          initial={tradeModal}
          onClose={() => setTradeModal(null)}
          onDone={() => { setTradeModal(null); load(); loadTrades(); }}
        />
      )}
      {showHistory && <HistoryModal trades={trades} onClose={() => setShowHistory(false)} />}
    </div>
  );
}

function Tile({ label, value, sub, tone, icon: Icon, ...rest }) {
  const cls = tone === "up" ? "text-signal-up" : tone === "down" ? "text-signal-down" : "text-text-primary";
  return (
    <div className="bg-bg-panel border border-bg-border rounded-xl p-4" {...rest}>
      <div className="text-[10px] uppercase tracking-wider text-text-secondary flex items-center gap-1">
        {Icon && <Icon size={11} className={cls} />} {label}
      </div>
      <div className={`text-xl font-mono font-bold mt-1 ${cls}`}>{value}</div>
      {sub && <div className="text-[11px] text-text-secondary mt-0.5">{sub}</div>}
    </div>
  );
}

function TradeModal({ initial, onClose, onDone }) {
  const [symbol, setSymbol] = useState(initial.symbol);
  const [side, setSide] = useState(initial.side);
  const [qty, setQty] = useState(10);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState(null);

  useEffect(() => {
    if (!symbol) { setPrice(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/symbol/${symbol}?period=3m`);
        if (alive) setPrice(data?.indicators?.close);
      } catch { if (alive) setPrice(null); }
    })();
    return () => { alive = false; };
  }, [symbol]);

  const submit = async () => {
    if (!symbol) return;
    setBusy(true);
    try {
      await api.post("/portfolio/trade", { symbol, side, quantity: Number(qty) });
      toast.success(`${side === "buy" ? "Alım" : "Satım"} gerçekleşti: ${symbol} x${qty}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally { setBusy(false); }
  };

  const total = (price ?? 0) * Number(qty || 0);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="trade-modal"
        className="w-full max-w-md bg-bg-panel border border-bg-border rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-bg-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">Yeni İşlem</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-1 bg-bg-base p-1 rounded-lg border border-bg-border">
            <button
              data-testid="trade-side-buy"
              onClick={() => setSide("buy")}
              className={`flex-1 py-2 text-sm rounded-md ${side === "buy" ? "bg-signal-up text-white" : "text-text-secondary"}`}
            >Al</button>
            <button
              data-testid="trade-side-sell"
              onClick={() => setSide("sell")}
              className={`flex-1 py-2 text-sm rounded-md ${side === "sell" ? "bg-signal-down text-white" : "text-text-secondary"}`}
            >Sat</button>
          </div>

          <div>
            <label className="block text-xs text-text-secondary mb-1.5">Hisse</label>
            <input
              data-testid="trade-symbol-input"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              list="ztan-symbol-list"
              className="w-full bg-bg-base border border-bg-border text-text-primary font-mono rounded-lg px-3 py-2.5 text-sm focus:border-brand-blue focus:outline-none"
              placeholder="Örn: AKBNK"
            />
            <datalist id="ztan-symbol-list">
              {BIST_QUICK.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>

          <div>
            <label className="block text-xs text-text-secondary mb-1.5">Adet</label>
            <input
              data-testid="trade-qty-input"
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="w-full bg-bg-base border border-bg-border text-text-primary font-mono rounded-lg px-3 py-2.5 text-sm focus:border-brand-blue focus:outline-none"
            />
          </div>

          <div className="bg-bg-base border border-bg-border rounded-lg p-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-text-secondary text-xs"><span>Fiyat</span><span className="font-mono text-text-primary">{price ? `₺${fmt(price)}` : "-"}</span></div>
            <div className="flex justify-between font-medium"><span className="text-text-primary">Toplam</span><span className={`font-mono ${side === "buy" ? "text-signal-down" : "text-signal-up"}`}>{side === "buy" ? "-" : "+"}₺{fmt(total)}</span></div>
          </div>

          <button
            data-testid="trade-submit-btn"
            onClick={submit}
            disabled={busy || !symbol || !qty || !price}
            className={`w-full py-2.5 rounded-lg text-sm font-bold transition-all disabled:opacity-50 ${
              side === "buy" ? "bg-signal-up text-white hover:opacity-90" : "bg-signal-down text-white hover:opacity-90"
            }`}
          >{busy ? "İşleniyor..." : side === "buy" ? "Alımı Onayla" : "Satışı Onayla"}</button>
        </div>
      </div>
    </div>
  );
}

function HistoryModal({ trades, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="portfolio-history-modal" className="w-full max-w-2xl max-h-[80vh] bg-bg-panel border border-bg-border rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-bg-border flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2"><History size={16} /> İşlem Geçmişi</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto">
          {trades.length === 0 ? (
            <div className="p-12 text-center text-sm text-text-secondary">Henüz işlem yok</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary text-xs uppercase tracking-wider border-b border-bg-border">
                  <th className="py-3 px-4 text-left">Tarih</th>
                  <th className="py-3 px-4 text-left">Hisse</th>
                  <th className="py-3 px-4 text-center">İşlem</th>
                  <th className="py-3 px-4 text-right">Adet</th>
                  <th className="py-3 px-4 text-right">Fiyat</th>
                  <th className="py-3 px-4 text-right">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <tr key={t.id} className="border-b border-bg-border">
                    <td className="py-2.5 px-4 text-text-secondary text-xs">{fmtDate(t.executed_at)}</td>
                    <td className="py-2.5 px-4 font-mono font-bold text-text-primary">{t.symbol}</td>
                    <td className="py-2.5 px-4 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${
                        t.side === "buy" ? "text-signal-up bg-signal-up/10 border-signal-up/30" : "text-signal-down bg-signal-down/10 border-signal-down/30"
                      }`}>{t.side === "buy" ? "AL" : "SAT"}</span>
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono text-text-primary">{fmt(t.quantity, 0)}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-text-primary">₺{fmt(t.price)}</td>
                    <td className={`py-2.5 px-4 text-right font-mono font-medium ${t.side === "buy" ? "text-signal-down" : "text-signal-up"}`}>
                      {t.side === "buy" ? "-" : "+"}₺{fmt(t.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function fmt(v, d = 2) {
  if (v == null) return "-";
  return Number(v).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function SectorPie({ positions }) {
  const [sectorMap, setSectorMap] = React.useState({});
  React.useEffect(() => { _loadSectorMap().then(setSectorMap); }, []);
  const groups = {};
  positions.forEach((p) => {
    const sec = sectorMap[p.symbol] || "Diğer";
    groups[sec] = (groups[sec] || 0) + p.market_value;
  });
  const total = positions.reduce((s, p) => s + p.market_value, 0) || 1;
  const chartData = Object.entries(groups)
    .map(([name, value]) => ({ name, value, pct: (value / total) * 100 }))
    .sort((a, b) => b.value - a.value);
  if (chartData.length === 0) return null;
  return (
    <div className="bg-bg-panel border border-bg-border rounded-xl p-4" data-testid="portfolio-sector-pie">
      <h3 className="text-sm font-semibold text-text-primary flex items-center gap-1.5 mb-3">
        Portföy Sektör Dağılımı
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
        <div style={{ height: 240 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} stroke="rgb(var(--bg-panel))" strokeWidth={3}>
                {chartData.map((entry, i) => (
                  <Cell key={entry.name} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />
                ))}
              </Pie>
              <RTooltip
                contentStyle={{ background: "rgb(var(--bg-panel))", border: "1px solid rgb(var(--bg-border))", borderRadius: 8, color: "rgb(var(--text-primary))" }}
                formatter={(v, n) => [`₺${Number(v).toLocaleString("tr-TR", {maximumFractionDigits:0})}`, n]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1.5">
          {chartData.map((it, i) => (
            <div key={it.name} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: SECTOR_COLORS[i % SECTOR_COLORS.length] }} />
                <span className="text-text-primary">{it.name}</span>
              </div>
              <div className="flex items-center gap-2 font-mono text-text-secondary">
                <span>₺{Number(it.value).toLocaleString("tr-TR", {maximumFractionDigits:0})}</span>
                <span className="text-accent-yellow font-medium">{it.pct.toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
